import React from 'react';
import PropTypes from 'prop-types';
import CwTextField from '../cwtextfield/CwTextField';
import {
  formatToAFloat, checkNextPropsWhileNotTyping, checkAcceptableChangeState, checkDecimalAgainstMaxLength,
  checkForOneDecimal, regexCreator, checkForOneNegativeAtProperLocation, generateMaxLength, checkforOneDecimalAndMaxDecimalUnits,
  checkForLeftSideLength
} from 'src/utils/NumberUtils';
import { AlignmentEnum } from '../../utils/Enums';

class CwNumberField extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      value: this.props.value || this.props.value === 0 ? this.props.formatNumber(this.props.value) : '',
      regex: this.props.regex ? this.props.regex : regexCreator(this.props.negative, this.props.decimal, this.props.thousands),
      prevValue: this.props.value ? this.props.formatNumber(this.props.value) : '',
    };
    this._typing = false;
    this._focused = false;
    this.validButNotANumber = false;
  }

  componentWillReceiveProps(nextProps) {
    if (checkNextPropsWhileNotTyping(nextProps.value, this._typing)) {
      this.setState({
        value: this.props.formatNumber(nextProps.value),
      });
    } else if ((nextProps.value || nextProps.value === '' || nextProps.value === 0) && (!this.state.value || this.state.value === '')) {
      this.setState({
        value: nextProps.value.toString(),
        prevValue: nextProps.value.toString(),
      });
    } else if (!nextProps.value && nextProps.value !== 0 && !this.validButNotANumber) {
      this.setState({
        value: ''
      });
    }
  }

  componentDidUpdate() {
    if (this.props.selectAllOnFocus && this._focused && !this._typing) {
      this.textField.inputField.select();
    }
  }

  handleChange = (value) => {

    //Keep acceptable states and bypass all other checks
    if (checkAcceptableChangeState(value, this.props.negative, this.props.decimal)) {
      this.setState({
        value: value,
      });
      this.validButNotANumber = true;
      this.props.onChange(null);
      return;
    }
    this.validButNotANumber = false;

    //only allow one negative symbol
    if (checkForOneNegativeAtProperLocation(value, this.props.negative, this.props.negativeAtStart)) {
      return;
    }

    if (!checkDecimalAgainstMaxLength(value, this.props.decimal,
      generateMaxLength(this.state.value, this.props.maxLength, this.props.decimal, this.props.maxDecimalUnits))) {

      this.props.onChange(this.formatToFloat(this.props.value.toString()));
      return;
    }

    if (this.props.maxDecimalUnits) {
      if (checkforOneDecimalAndMaxDecimalUnits(value, this.props.decimal, this.props.maxDecimalUnits)) {
        return;
      }
    }
    //only allow one decimal
    if (checkForOneDecimal(value, this.props.decimal)) {
      return;
    }

    if (!checkForLeftSideLength(value, this.props.maxLength, this.props.decimal)) {
      return;
    }

    //Guard against NaN and pass up null if we cannot format things to a float.
    if (isNaN(this.formatToFloat(value))) {
      this.setState({
        value: '',
      });
      this.props.onChange(null);
      return;
    }

    this.setState({
      value: value,
    });
    this.props.onChange(this.formatToFloat(value));
  };

  //This method gets called when more then one negative or decimal are used
  restrictLastDigit = (value) => {
    const newVal = value.substring(0, value.length - 1);
    this.setState({
      value: newVal,
    });
    if (isNaN(this.formatToFloat(newVal))) {
      this.validButNotANumber = true;
      this.props.onChange(null);
      return;
    }

    this.props.onChange(this.formatToFloat(newVal));
  };

  focus() {
    this.textField.focus();
  }

  handleFocus = () => {
    this._focused = true;
    this.setState((prevState) => {
      return {
        prevValue: prevState.value,
      };
    });

    if (this.props.formatOnFocus) {
      const newVal = this.props.formatOnFocus(this.state.value);
      this.setState({
        value: newVal
      });
    }

    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleBlur = () => {
    this._typing = false;
    this._focused = false;
    if (!this.state.value) {
      this.setState({
        value: '',
      });
      /* istanbul ignore next */
      if (this.props.onBlur) {
        this.props.onBlur();
      }
      return;
    }
    if (!this.validButNotANumber) {
      this.setState({
        value: this.props.formatNumber(this.formatToFloat(this.state.value))
      });
    } else {
      this.setState({
        value: ''
      });
    }

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleKeyDown = () => {
    this._typing = true;
    if (this.props.onKeyDown) {
      this.props.onKeyDown();
    }
  }

  /**
   * Format a string to a float by stripping out all special characters and passing it to format float
   */
  formatToFloat = (value) => {
    if (!value || typeof value !== "string") {
      return 0;
    }
    return formatToAFloat(value, this.props.thousands, this.props.decimal, this.props.negative);
  };

  render() {

    return (
      <CwTextField
        ref={(item) => this.textField = item}
        cwId={this.props.cwId}
        value={this.state.value}
        onChange={this.handleChange}
        regex={this.state.regex}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        maxLength={generateMaxLength(this.state.value, this.props.maxLength, this.props.decimal, this.props.maxDecimalUnits)}
        placeholder={this.props.placeholder}
        readOnly={this.props.readOnly}
        onKeyPress={this.props.onKeyPress}
        tabIndex={this.props.tabIndex}
        invalid={this.props.invalid}
        disabled={this.props.disabled}
        id={this.props.id}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.props.onKeyUp}
        autoFocus={this.props.autoFocus}
        textAlignment={this.props.textAlignment ? this.props.textAlignment : this.props.inAGrid ? AlignmentEnum.RIGHT : undefined}
        style={this.props.style}
      />
    );
  }
  ;
}

CwNumberField.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Text in textfield
   */
  value: PropTypes.number,
  /**
   * Hint text, displayed when user has not typed anything in
   */
  placeholder: PropTypes.string,
  /**
   * Used to set textfield to a read only mode
   */
  readOnly: PropTypes.bool,
  /**
   * Callback function that is fired when textfield's text changes
   */
  onChange: PropTypes.func,
  /**
   * Callback function that is fired when any key is pressed
   */
  onKeyPress: PropTypes.func,
  /**
   * Used to control order when tabbing through items
   */
  tabIndex: PropTypes.number,
  /**
   * Used to control if text is valid
   */
  invalid: PropTypes.bool,
  /**
   * Used to disable textfield
   */
  disabled: PropTypes.bool,
  /**
   * Used as an identifier for the component
   */
  id: PropTypes.string,
  /**
   * Used to limit the inputs of the field by matching against provided string.
   */
  regex: PropTypes.string,
  /**
   * Callback function that is fired when textfield is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when textfield is blurred
   */
  onBlur: PropTypes.func,
  /**
   * Callback function that is fired on key down
   */
  onKeyDown: PropTypes.func,
  /**
   * Callback function that is fired on key up
   */
  onKeyUp: PropTypes.func,
  /**
   * Change the input field type
   */
  type: PropTypes.string,
  /**
   * Used to control if numberfield is autofocused when the page loads
   */
  autoFocus: PropTypes.bool,
  /**
   * The maximium number of characters allowed in the field. Can never exceed 16
   */
  maxLength: PropTypes.number,
  /**
   * The negative symbol of the native locale
   */
  negative: PropTypes.string.isRequired,
  /**
   * The decimal symbol of the native locale
   */
  decimal: PropTypes.string.isRequired,
  /**
   * The thousands separator of the native locale
   */
  thousands: PropTypes.string.isRequired,
  /**
   * A function used to format the internal number value to a localized display string.
   */
  formatNumber: PropTypes.func.isRequired,
  /**
   * A bool to determine whether the negative symbol goes at the start or end of a string"
   */
  negativeAtStart: PropTypes.bool,
  /**
   * Format the internal value if need be. 
   */
  formatOnFocus: PropTypes.func,
  /**
   * The maximum number of places after the decimal
   * which the number field accepts.
   */
  maxDecimalUnits: PropTypes.number,
  /**
   * Override the styles of the root element
   */
  style: PropTypes.object,
  /**
   * Used to control if all text is highlighted on focus
   */
  selectAllOnFocus: PropTypes.bool
};

CwNumberField.defaultProps = {
  negative: '-',
  decimal: '.',
  thousands: ',',
  maxLength: 16,
  negativeAtStart: true
};

export default CwNumberField;


// WEBPACK FOOTER //
// ./src/components/cwnumberfield/CwNumberField.jsx